// src/services/api.js
import axios from 'axios';

// ============================================================
// BASE URL CONFIGURATION
// ============================================================
const getBaseURL = () => {
  // Priorité 1: Variable d'environnement
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/$/, '');
  }
  
  // Priorité 2: Détection automatique selon l'environnement
  const isDev = process.env.NODE_ENV === 'development';
  const hostname = window.location.hostname;
  
  if (isDev) {
    // En dev local, utiliser localhost:5000
    return 'http://localhost:5000/api';
  }
  
  // En production, utiliser le sous-domaine api
  if (hostname === 'maxula.halamall.shop' || hostname === 'www.maxula.halamall.shop') {
    return 'https://api.maxula.halamall.shop/api';
  }
  
  // Fallback
  return '/api';
};

const baseURL = getBaseURL();

console.log('🔧 API Base URL:', baseURL);

const API = axios.create({
  baseURL,
  headers: { Accept: 'application/json' },
  timeout: 30000,
  withCredentials: true
});

// ============================================================
// TOKEN EXTRACTION
// ============================================================
const extractToken = () => {
  try {
    const raw = localStorage.getItem('token');
    if (raw) return raw;
  } catch {}

  try {
    const stored = localStorage.getItem('auth') || localStorage.getItem('user');
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed?.token || parsed?.accessToken || parsed?.jwt || null;
    }
  } catch {}

  return null;
};

const isObjectId = (v) => typeof v === 'string' && /^[0-9a-fA-F]{24}$/.test(v);

// ============================================================
// REQUEST INTERCEPTOR
// ============================================================
API.interceptors.request.use(
  (config) => {
    const token = extractToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    // Fix double /api/api/
    const base = config.baseURL || '';
    const url = config.url || '';
    if (base.endsWith('/api') && url.startsWith('/api/')) {
      config.url = url.replace(/^\/api/, '');
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ============================================================
// RESPONSE INTERCEPTOR
// ============================================================
API.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error?.response?.status;

    if (status === 401) {
      localStorage.removeItem('token');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// ============================================================
// API ENDPOINTS
// ============================================================
export const authAPI = {
  login: (data) => API.post('/auth/login', data),
  register: (data) => API.post('/auth/register', data),
  getMe: async () => {
    const candidates = ['/auth/me', '/users/me', '/me'];
    let lastErr;
    for (const ep of candidates) {
      try {
        return await API.get(ep);
      } catch (err) {
        lastErr = err;
      }
    }
    throw lastErr;
  },
  forgotPassword: (data) => API.post('/auth/forgot-password', data),
  resetPassword: (token, data) => API.put(`/auth/reset-password/${token}`, data)
};

export const dealAPI = {
  getAll: (params) => API.get('/deals', { params }),
  getOne: (slugOrId) =>
    isObjectId(slugOrId)
      ? API.get(`/deals/id/${slugOrId}`)
      : API.get(`/deals/slug/${encodeURIComponent(slugOrId)}`),
  getFeatured: () => API.get('/deals/featured'),
  getByCategory: (slugOrId) =>
    isObjectId(slugOrId)
      ? API.get(`/deals/category/${slugOrId}`)
      : API.get(`/deals/category/${encodeURIComponent(slugOrId)}`),
  create: (data) => API.post('/deals', data),
  update: (id, data) => API.put(`/deals/id/${id}`, data),
  delete: (id) => API.delete(`/deals/id/${id}`)
};

export const orderAPI = {
  create: (data) => API.post('/orders', data),
  getAll: (params) => API.get('/orders', { params }),
  getOne: (id) => API.get(`/orders/${id}`),
  cancel: (id) => API.put(`/orders/${id}/cancel`)
};

export const userAPI = {
  getProfile: () => API.get('/users/profile'),
  updateProfile: (data) => API.put('/users/profile', data),
  updatePassword: (data) => API.put('/users/password', data),
  getOrders: () => API.get('/users/orders'),
  getVouchers: (status) => API.get('/users/vouchers', { params: { status } }),
  getFavorites: () => API.get('/users/favorites'),
  addFavorite: (dealId) => API.post(`/users/favorites/${dealId}`),
  removeFavorite: (dealId) => API.delete(`/users/favorites/${dealId}`)
};

export const categoryAPI = {
  getAll: () => API.get('/categories'),
  getOne: (slugOrId) =>
    isObjectId(slugOrId)
      ? API.get(`/categories/${slugOrId}`)
      : API.get(`/categories/slug/${encodeURIComponent(slugOrId)}`)
};

export const cityAPI = {
  getAll: (params) => API.get('/cities', { params }),
  getOne: (slug) => API.get(`/cities/slug/${slug}`),
  create: (data) => API.post('/cities', data),
  update: (id, data) => API.put(`/cities/${id}`, data),
  delete: (id) => API.delete(`/cities/${id}`),
  toggle: (id) => API.patch(`/cities/${id}/toggle`)
};


export const adminAPI = {
  updateUserRole: (id, data) => API.patch(`/admin/users/${id}/role`, data),
  getDashboard: () => API.get('/admin/dashboard'),
  getAnalytics: (params) => API.get('/admin/analytics', { params }),
  getUsers: (params) => API.get('/admin/users', { params }),
  getUserStats: () => API.get('/admin/users/stats'),
  updateUserStatus: (id, data) => API.patch(`/admin/users/${id}/status`, data),
  getMerchants: (params) => API.get('/admin/merchants', { params }),
  approveMerchant: (id) => API.put(`/admin/merchants/${id}/approve`),
  suspendMerchant: (id) => API.put(`/admin/merchants/${id}/suspend`),
  getDeals: (params) => API.get('/admin/deals', { params }),
  approveDeal: (id) => API.put(`/admin/deals/${id}/approve`),
  updateDealStatus: (id, data) => API.patch(`/admin/deals/${id}/status`, data),
  deleteDeal: (id) => API.delete(`/admin/deals/${id}`),
  getOrders: (params) => API.get('/admin/orders', { params }),
  getReports: (params) => API.get('/admin/reports', { params }),
  getReviews: (params) => API.get('/admin/reviews', { params }),
  approveReview: (id) => API.patch(`/admin/reviews/${id}/approve`),
  rejectReview: (id) => API.patch(`/admin/reviews/${id}/reject`),
  deleteReview: (id) => API.delete(`/admin/reviews/${id}`),
  getNotifications: (params) => API.get('/admin/notifications', { params }),
  getUnreadNotificationsCount: (params) =>
    API.get('/admin/notifications/unread-count', { params }),
  markAsRead: (id) => API.patch(`/admin/notifications/${id}/read`),
  markAllAsRead: (params) =>
    API.patch('/admin/notifications/read-all', null, { params }),
  deleteNotification: (id) => API.delete(`/admin/notifications/${id}`),
  deleteReadNotifications: (params) =>
    API.delete('/admin/notifications', { params })
};

export const merchantAPI = {
  getAll: (params) => API.get('/merchants', { params }),
  getOne: (id) => API.get(`/merchants/${id}`),
  create: (data) => API.post('/merchant/register', data),
  update: (id, data) => API.put(`/merchants/${id}`, data),
  verify: (id) => API.put(`/merchants/${id}/verify`),
  getCurrentDeals: () => API.get('/merchant/me/deals'),
  getCurrentDashboard: () => API.get('/merchant/dashboard')
};

export const paymentAPI = {
  createPayment: (data) => API.post('/payment/initiate', data),
  verifyPayment: (data) => API.post('/payment/verify', data),
  getPaymentStatus: (orderId) => API.post('/payment/verify', { orderId })
};

export const reviewAPI = {
  create: (data) => API.post('/reviews', data),
  getByDeal: (dealId) => API.get(`/reviews/deal/${dealId}`),
  update: (id, data) => API.put(`/reviews/${id}`, data),
  delete: (id) => API.delete(`/reviews/${id}`)
};

export const voucherAPI = {
  getAll: () => API.get('/vouchers'),
  getOne: (code) => API.get(`/vouchers/${encodeURIComponent(code)}`),
  use: (code) => API.post(`/vouchers/${encodeURIComponent(code)}/use`)
};

export default API;